import { HttpResponse } from '@angular/common/http';
import { Alert, AlertDates } from '@features/researches/models/alert.model';
import { Report, ReportStatus, ReportToUpsert } from '@features/researches/models/report.model';
import {
  AlertsRequestData,
  RequestData,
  SensorsFilters,
  SensorsRequestData
} from '@features/researches/models/request-data.model';
import {
  CreatorOrganisationalStructure,
  CreatorResearchDetails,
  CreatorSpacePlan,
  CreatorSpaceUtilization,
  CreatorStep,
  CreatorWorkspaceDetails
} from '@features/researches/models/research-creator.model';
import {
  Industry,
  Participants,
  ResearchWithId,
  SearchableResearch,
  TechnicianNotifications,
  UserEmail
} from '@features/researches/models/research.model';
import {
  Sensor,
  SensorAllDetails,
  SensorSetupType,
  SensorsStatistics,
  SensorStatus
} from '@features/researches/models/sensor.model';
import { Timezone } from '@features/researches/models/timezone.model';
import { createAction, props } from '@ngrx/store';
import { DateRangeModel } from '@shared/models/date-range.model';
import { ReportType, ResearchReport } from '@shared/models/report.model';
import { PageData } from '@shared/paging/page-data';

export const addIndustry = createAction('[ResearchDetailsStep] add industry', props<{ industry: Industry }>());

export const initResearchCreator = createAction('[Researches] init research creator');
export const getIndustries = createAction('[Researches] get industries');
export const getIndustriesSuccess = createAction(
  '[Researches] get industries success',
  props<{ industries: Industry[] }>()
);
export const getIndustriesFailure = createAction('[Researches] get industries failure');

export const changeResearchDetailsStepToNext = createAction(
  '[ResearchDetailsStep] change research details step to next',
  props<{ step: CreatorResearchDetails }>()
);
export const changeWorkspaceDetailsStepToNext = createAction(
  '[WorkspaceDetailsStep] change workspace details step to next',
  props<{ step: CreatorWorkspaceDetails }>()
);
export const changeWorkspaceDetailsStepToPrevious = createAction(
  '[WorkspaceDetailsStep] change workspace details step to previous',
  props<{ step?: CreatorWorkspaceDetails }>()
);
export const changeOrganisationalStructureStepToNext = createAction(
  '[OrganisationalStructureStep] change organisational structure step to next',
  props<{ step: CreatorOrganisationalStructure }>()
);
export const changeOrganisationalStructureStepToPrevious = createAction(
  '[OrganisationalStructureStep] change organisational structure step to previous',
  props<{ step?: CreatorOrganisationalStructure }>()
);
export const changeSpaceUtilizationStepToNext = createAction(
  '[SpaceUtilizationStep] change space utilization step to next',
  props<{ step: CreatorSpaceUtilization }>()
);
export const changeSpaceUtilizationStepToPrevious = createAction(
  '[SpaceUtilizationStep] change space utilization step to previous',
  props<{ step?: CreatorSpaceUtilization }>()
);

export const changeSpacePlanStepToPrevious = createAction(
  '[SpacePlanStep] change space plan step to previous',
  props<{
    utilisedSpacesSensors: { teamId: string; sensorsData: Record<string, Sensor> }[];
  }>()
);

export const saveSpacePlan = createAction('[SpacePlanStep] save space plan', props<{ file: File; fileName: string }>());
export const saveCompanyLogo = createAction('[ResearchDetailsStep] save company logo', props<{ file: File }>());
export const getResearches = createAction('[Researches] get researches', props<{ requestData?: RequestData }>());
export const getResearchesSuccess = createAction(
  '[Researches] get researches success',
  props<{ researches: SearchableResearch[] }>()
);
export const getResearchesFailure = createAction('[ResearchesPreview] get researches failure');
export const getResearchForPreview = createAction(
  '[Researches] get research for preview',
  props<{ researchId: number }>()
);
export const getResearchForPreviewSuccess = createAction(
  '[Research] get research for preview success',
  props<{ research: ResearchWithId }>()
);
export const getResearchForPreviewFailure = createAction('[ResearchPreview] get research for preview failure');

export const saveResearchDraft = createAction(
  '[ResearchCreator] save draft',
  props<{
    stepName: CreatorStep;
    step:
      | CreatorResearchDetails
      | CreatorWorkspaceDetails
      | CreatorOrganisationalStructure
      | CreatorSpaceUtilization
      | CreatorSpacePlan;
  }>()
);
export const saveResearchDraftSuccess = createAction(
  '[ResearchCreator] save draft success',
  props<{ researchId: number }>()
);
export const saveResearchDraftFailure = createAction('[ResearchCreator] save draft failure');

export const getResearchForDraftEdit = createAction(
  '[Researches] get research for draft edit',
  props<{ researchId: number }>()
);
export const getResearchForDraftEditSuccess = createAction(
  '[Research] get research for draft edit success',
  props<{ research: ResearchWithId; companyLogoFile: File | null; spacePlanFileName: string }>()
);
export const getResearchForDraftEditFailure = createAction('[ResearchPreview] get research for draft edit failure');

export const saveResearchSetup = createAction(
  '[ResearchCreator] save research setup',
  props<{
    utilisedSpacesSensors: { teamId: string; sensorsData: Record<string, Sensor>; locationId: string }[];
  }>()
);
export const saveResearchSetupSuccess = createAction(
  '[ResearchCreator] save research setup success',
  props<{ researchId: number }>()
);
export const saveResearchSetupFailure = createAction('[ResearchCreator] save research setup failure');

export const getTechnicianNotifications = createAction(
  '[ResearchPreview] get technician notifications',
  props<{ researchId: number }>()
);
export const getTechnicianNotificationsSuccess = createAction(
  '[ResearchPreview] get technician notifications success',
  props<{ notifications: string[] }>()
);
export const getTechnicianNotificationsFailure = createAction('[ResearchPreview] get technician notifications failure');
export const sendTechniciansNotifications = createAction(
  '[ResearchPreview] send technician notifications',
  props<{ notifications: TechnicianNotifications; researchId: number }>()
);
export const sendTechniciansNotificationsSuccessAndEmailSend = createAction(
  '[ResearchPreview] send technician notifications success and email send',
  props<{ researchId: number }>()
);
export const sendTechniciansNotificationsSuccessAndEmailNotSend = createAction(
  '[ResearchPreview] send technician notifications success and email not send'
);
export const sendTechniciansNotificationsFailure = createAction(
  '[ResearchPreview] send technician notifications failure'
);

export const sendUsersInvitations = createAction(
  '[ResearchPreview] send users invitations',
  props<{ participants: Participants; researchId: number }>()
);
export const sendUsersInvitationsSuccess = createAction(
  '[ResearchPreview] send users invitations success',
  props<{ researchId: number }>()
);
export const sendUsersInvitationsFailure = createAction('[ResearchPreview] send users invitations failure');
export const getResearchParticipants = createAction(
  '[ResearchPreview] get research participants',
  props<{ researchId: number }>()
);
export const getResearchParticipantsSuccess = createAction(
  '[ResearchPreview] get research participants success',
  props<{ invitations: UserEmail[] }>()
);
export const getResearchParticipantsFailure = createAction('[ResearchPreview] get research participants failure');

export const getResearchForSensorsSetup = createAction(
  '[SensorsSetup] get research for sensors setup',
  props<{ researchId: number }>()
);
export const getResearchForSensorsSetupSuccess = createAction(
  '[SensorsSetup] get research for sensors setup success',
  props<{ research: ResearchWithId }>()
);
export const getResearchForSensorsSetupFailure = createAction('[SensorsSetup] get research for sensors setup failure');

export const getResearchReports = createAction(
  '[ResearchPreview] get research reports',
  props<{ researchId: number }>()
);

export const getResearchReportsSuccess = createAction(
  '[ResearchPreview] get research reports success',
  props<{ reports: Report[] }>()
);

export const getResearchReportsFailure = createAction('[ResearchPreview] get research reports failure');

export const saveResearchReport = createAction(
  '[ResearchPreview] save research report',
  props<{ report: ReportToUpsert; researchId: number }>()
);
export const saveResearchReportSuccess = createAction(
  '[ResearchPreview] save research report success',
  props<{ researchId: number }>()
);

export const saveResearchReportFailure = createAction('[ResearchPreview] save research report failure');

export const deleteResearchReport = createAction(
  '[ResearchPreview] delete research report',
  props<{ researchId: number; reportId: number; reportName: string }>()
);
export const deleteResearchReportSuccess = createAction(
  '[ResearchPreview] delete research report success',
  props<{ researchId: number; reportName: string }>()
);

export const deleteResearchReportFailure = createAction('[ResearchPreview] delete research report failure');

export const editResearchReport = createAction(
  '[ResearchPreview] edit research report',
  props<{ researchId: number; reportId: number; report: ReportToUpsert }>()
);
export const editResearchReportSuccess = createAction(
  '[ResearchPreview] edit research report success',
  props<{ researchId: number; reportName: string }>()
);

export const editResearchReportFailure = createAction('[ResearchPreview] edit research report failure');

export const setupSensor = createAction(
  '[SensorSetup] setup sensor',
  props<{ id: string; macAddress: string; isAdditionalRoom: boolean; researchId: number }>()
);
export const setupSensorSuccess = createAction(
  '[SensorSetup] setup sensor success',
  props<{ id: string; macAddress: string; status: SensorStatus }>()
);
export const setupSensorFailure = createAction(
  '[SensorSetup] setup sensor failure',
  props<{
    researchId: number;
    sensorId: string;
    sensorSetupType: SensorSetupType;
    macAddress: string;
    httpCode: number;
  }>()
);

export const getResearchSpacePlan = createAction(
  '[SensorSetup] get research space plan',
  props<{ researchId: number | undefined; section: 'preview' | 'setup' }>()
);
export const getResearchSpacePlanSuccess = createAction(
  '[SensorSetup] get research space plan success',
  props<{ spacePlanFileUrl: string; spacePlanFileName: string; section: 'preview' | 'setup' }>()
);
export const getResearchSpacePlanFailure = createAction('[SensorSetup] get research space plan failure');

export const activateResearch = createAction('[ResearchPreview] activate research', props<{ researchId: number }>());
export const activateResearchSuccess = createAction(
  '[ResearchPreview] activate research success',
  props<{ researchId: number }>()
);
export const activateResearchFailure = createAction('[ResearchPreview] activate research failure');

export const editResearch = createAction('[ResearchPreview] edit research', props<{ researchId: number }>());
export const editResearchSuccess = createAction(
  '[ResearchPreview] edit research success',
  props<{ research: ResearchWithId; companyLogoFile: File | null; spacePlanFileName: string }>()
);
export const editResearchFailure = createAction('[ResearchPreview] edit research failure');
export const generateReport = createAction(
  '[Reports] generate report',
  props<{ reportAsBlob: Blob; reportType: ReportType; reportId: number; researchId: number; reportName: string }>()
);
export const generateReportSuccess = createAction(
  '[Reports] generate report success',
  props<{ reportType: ReportType; reportId: number; reportAsBlob: Blob; reportName: string }>()
);
export const generateReportFailure = createAction('[Reports] generate report failure', props<{ reportName: string }>());
export const changeReportStatus = createAction(
  '[Reports] change report status',
  props<{ newStatus: ReportStatus; reportId: number; reportType: ReportType }>()
);

export const completeDeviceSetup = createAction('[SensorSetup] complete device setup', props<{ researchId: number }>());
export const completeDeviceSetupSuccess = createAction(
  '[SensorSetup] complete device setup success',
  props<{ researchId: number }>()
);
export const completeDeviceSetupFailure = createAction('[SensorSetup] complete device setup failure');
export const publishReport = createAction(
  '[Reports] publish report',
  props<{ researchId: number; reportId: number; reportType: ReportType }>()
);
export const publishReportSuccess = createAction(
  '[Reports] publish report success',
  props<{ reportId: number; reportType: ReportType }>()
);
export const publishReportFailure = createAction('[Reports] publish report failure');

export const previewReport = createAction(
  '[Reports] preview report',
  props<{ researchId: number; reportId: number }>()
);
export const previewReportSuccess = createAction('[Reports] preview report success', props<{ reportAsBlob: Blob }>());
export const previewReportFailure = createAction('[Reports] preview report failure');

export const downloadAnalyticalDataTemplateFile = createAction(
  '[ResearchPreview] download analytical data template file',
  props<{ researchId: number; dateRange: DateRangeModel }>()
);

export const downloadAnalyticalDataTemplateFileSuccess = createAction(
  '[ResearchPreview] download analytical data template file success',
  props<{ templateDataResponse: HttpResponse<Object>; fileName: string }>()
);

export const downloadAnalyticalDataTemplateFileFailure = createAction(
  '[ResearchPreview] download analytical data template file failure'
);

export const uploadAnalyticalDataFile = createAction(
  '[ResearchPreview] upload analytical data file',
  props<{ researchId: number; file: File }>()
);

export const uploadAnalyticalDataFileSuccess = createAction(
  '[ResearchPreview] upload analytical data file success',
  props<{ analyticsDataResponse: HttpResponse<Object>; fileName: string }>()
);

export const uploadAnalyticalDataFileFailure = createAction('[ResearchPreview] upload analytical data file failure');

export const archiveResearch = createAction('[Researches] archive research', props<{ researchId: number }>());
export const archiveResearchSuccess = createAction(
  '[Researches] archive research success',
  props<{ researchId: number }>()
);
export const archiveResearchFailure = createAction('[Researches] archive research failure');

export const getSensorsAlerts = createAction(
  '[Alerts] get sensors alerts',
  props<{ requestData: AlertsRequestData }>()
);
export const getSensorsAlertsSuccess = createAction(
  '[Alerts] get sensors alerts success',
  props<{ alerts: PageData<Alert> }>()
);
export const getSensorsAlertsFailure = createAction('[Alerts] get sensors alerts failure');

export const getAlertsDateRange = createAction('[Alerts] get alerts date range');
export const getAlertsDateRangeSuccess = createAction(
  '[Alerts] get alerts date range success',
  props<{ dateRange: AlertDates }>()
);
export const getAlertsDateRangeFailure = createAction('[Alerts] get alerts date range failure');

export const downloadFinalReport = createAction('[Researches] download final report', props<{ researchId: number }>());
export const downloadFinalReportSuccess = createAction(
  '[Researches] download final report success',
  props<{ reportAsBlob: Blob }>()
);
export const downloadFinalReportFailure = createAction('[Researches] download final report failure');

export const getAllSensors = createAction('[Sensors] get all sensors', props<{ requestData: SensorsRequestData }>());
export const getAllSensorsSuccess = createAction(
  '[Sensors] get all sensors success',
  props<{ sensorsData: PageData<SensorAllDetails> }>()
);
export const getAllSensorsFailure = createAction('[Sensors] get all sensors failure');

export const getSensorsFile = createAction('[Sensors] get sensors file', props<{ requestData?: SensorsFilters }>());
export const getSensorsFileSuccess = createAction(
  '[Sensors] get sensors file success',
  props<{ sensorsFileResponse: HttpResponse<Object>; fileName: string }>()
);
export const getSensorsFileFailure = createAction('[Sensors] get sensors file failure');

export const getSensorsStatistics = createAction('[Sensors] get sensors statistics');
export const getSensorsStatisticsSuccess = createAction(
  '[Sensors] get sensors statistics success',
  props<{ sensorsStatistics: SensorsStatistics }>()
);
export const getSensorsStatisticsFailure = createAction('[Sensors] get sensors statistics failure');

export const getDataForReportGeneration = createAction(
  '[Report] get data for report generation',
  props<{ researchId: number; reportId: number; reportType: ReportType; reportName: string }>()
);
export const getDataForReportGenerationSuccess = createAction(
  '[Report] get data for report generation success',
  props<{
    reportData: ResearchReport;
    reportType: ReportType;
    researchId: number;
    reportId: number;
    reportName: string;
  }>()
);
export const getDataForReportGenerationFailure = createAction(
  '[Report] get data for report generation failure',
  props<{ reportName: string }>()
);
export const clearReportResearchData = createAction('[Report] clear data for report generation');

export const clearResearchPreview = createAction('[ResearchPreview] clear research preview');

export const getTimezones = createAction('[ResearchDetailsStep] get timezones');
export const getTimezonesSuccess = createAction(
  '[ResearchDetailsStep] get timezones success',
  props<{ timezones: Timezone[] }>()
);
export const getTimezonesFailure = createAction('[ResearchDetailsStep] get timezones failure');

export const unpairSensor = createAction(
  '[SensorSetup] unpair sensor',
  props<{ researchId: number; sensorMacAddress: string }>()
);
export const unpairSensorSuccess = createAction(
  '[SensorSetup] unpair sensor success',
  props<{ researchId: number; sensorMacAddress: string }>()
);
export const unpairSensorFailure = createAction('[SensorSetup] unpair sensor failure');
